import Modal from "components/DesignSystem/Modal/Modal";
import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { ModalProps } from "types/utils/modal";
import Desktop from "static/images/Desktop.svg";
import Folder from "static/images/Folder.svg";
import {
  UploadFromInkleDocsFileSelectorModal,
  UploadFromInkleDocsFileSelectorModalProps,
} from "components/UploadFromInkleDocsFileSelectorModal/UploadFromInkleDocsFileSelectorModal";
import { useModal } from "hooks/useModal";
import { SelecteFile } from "types/Models/documents";
import { FileType } from "components/FileInput/FileInput";
import classNames from "classnames";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";

type UploadFromSelectorModalProps = {
  allowSingleUpload?: boolean;
  onDrop: (files: FileType[]) => void;
  accept?: { [key: string]: string[] };
  withModal?: boolean;
  block?: boolean;
  allowUploadToFolder?: boolean;
  hideTitle?: boolean;
} & ModalProps &
  Pick<
    UploadFromInkleDocsFileSelectorModalProps,
    "fileTag" | "groupId" | "entityId" | "folderPath"
  >;

const UploadFromSelectorModalWrapper = ({
  isOpen,
  close,
  withModal,
  children,
}: {
  isOpen: boolean;
  close: () => void;
  withModal: boolean;
  children: React.ReactNode;
}) => {
  if (!withModal) {
    return <>{children}</>;
  }

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="regular">
        <Modal.Header>
          <Modal.Title>Upload from</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>{children}</Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

export const UploadFromSelectorModal = ({
  close,
  isOpen,
  allowSingleUpload = true,
  onDrop: onDropFromProps,
  groupId,
  fileTag,
  entityId,
  folderPath,
  accept = {
    "image/*": [".png", ".jpeg", ".jpg", ".webp", ".avif"],
    "application/pdf": [".pdf"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
    "text/csv": [".csv"],
  },
  withModal = true,
  block,
  allowUploadToFolder,
  hideTitle,
}: UploadFromSelectorModalProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const onDrop = (files: File[]) => {
    if (!allowUploadToFolder) {
      onDropFromProps(files);
      close();
    }
    setSelectedFiles(files);
    uploadToFolder.open();
  };

  const onUploadSuccess = (files: SelecteFile[]) => {
    onDropFromProps(files);
    close();
  };

  const { open, getRootProps, getInputProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop,
    maxSize: 25000000,
    accept,
    maxFiles: allowSingleUpload ? 1 : Infinity,
    multiple: !allowSingleUpload,
  });

  const uploadFromInkle = useModal();
  const uploadToFolder = useModal();

  return (
    <>
      <UploadFromSelectorModalWrapper
        isOpen={isOpen}
        close={close}
        withModal={withModal}
      >
        <div
          className="t-flex t-flex-col t-items-center t-justify-center t-gap-5"
          {...getRootProps()}
        >
          <button
            className={classNames(
              "all:unset t-flex t-cursor-pointer t-items-center t-justify-center t-gap-2 t-rounded-lg t-border-neutral-20 t-py-8 t-text-body-sm hover:t-border-purple-40 focus-visible:t-border-purple-40 t-bg-surface-lighter-grey upload-card-dashed-border",
              {
                "t-w-full": block,
                "t-w-96 t-max-w-full t-box-border": !block,
              }
            )}
            onClick={uploadFromInkle.open}
            title="Choose from Inkle Document"
          >
            <img src={Folder} alt="Inkle Doc" />
            Choose from Inkle Document
          </button>
          <div className="t-flex t-items-center t-gap-2 t-justify-center">
            <span className="t-w-8 t-h-[1px] t-bg-neutral-10"></span>
            <span className="t-text-body-sm t-text-text-30">or</span>
            <span className="t-w-8 t-h-[1px] t-bg-neutral-10"></span>
          </div>
          <button
            className={classNames(
              "all:unset t-flex t-cursor-pointer t-items-center t-justify-center t-gap-2 t-rounded-lg t-border-neutral-20 t-py-8 t-text-body-sm hover:t-border-purple-40 focus-visible:t-border-purple-40 t-bg-surface-lighter-grey upload-card-dashed-border",
              {
                "t-w-full": block,
                "t-w-96 t-max-w-full t-box-border": !block,
              }
            )}
            onClick={open}
            title={hideTitle ? "" : "Choose a file from computer"}
          >
            <input {...getInputProps()} data-testid="file-input" />
            <img src={Desktop} alt="device" />
            Choose a file from computer
          </button>
        </div>
      </UploadFromSelectorModalWrapper>
      {uploadFromInkle.isOpen && (
        <UploadFromInkleDocsFileSelectorModal
          close={uploadFromInkle.close}
          isOpen={uploadFromInkle.isOpen}
          onUpload={onUploadSuccess}
          groupId={groupId}
          fileTag={fileTag}
          entityId={entityId}
          folderPath={folderPath}
          allowSingleUpload={allowSingleUpload}
        />
      )}
      {allowUploadToFolder && uploadToFolder.isOpen && (
        <SelectFolderForUpload
          showModal={uploadToFolder.isOpen}
          closeModal={uploadToFolder.close}
          files={selectedFiles}
          onUploadSuccess={(file) => onUploadSuccess([file])}
          entityId={entityId}
          folderPath={folderPath}
        />
      )}
    </>
  );
};
